import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  mediaUploader,
  MediaUploadProgress,
} from "../providers/dataProvider/imageUploader";

export type UploadStatus =
  | "queued"
  | "uploading"
  | "processing"
  | "completed"
  | "error"
  | "uncertain"
  | "cancelled";

export type UploadRequest = {
  userId: string;
  userLabel: string;
  semana: number;
  categoryImage: string;
  categoryVideo: string;
  files: File[];
};

export type UploadJob = {
  id: string;
  request: UploadRequest;
  status: UploadStatus;
  loaded: number;
  total: number;
  progress: number | null;
  error?: string;
};

export type UploadManagerValue = {
  jobs: UploadJob[];
  enqueue: (request: UploadRequest) => string;
  cancel: (jobId: string) => void;
  retry: (jobId: string) => void;
  dismiss: (jobId: string) => void;
};

type UploadManagerProviderProps = PropsWithChildren<{
  onCompleted?: (job: UploadJob) => void;
  onFailed?: (job: UploadJob) => void;
}>;

const UploadManagerContext = createContext<UploadManagerValue | null>(null);

const uncertainResultMessage =
  "El servidor recibió todos los archivos, pero no pudimos confirmar el resultado. Comprueba la galería antes de volver a subirlos.";

export function UploadManagerProvider({
  children,
  onCompleted,
  onFailed,
}: UploadManagerProviderProps) {
  const [jobs, setJobs] = useState<UploadJob[]>([]);
  const sequence = useRef(0);
  const activeJobId = useRef<string | null>(null);
  const activeController = useRef<AbortController | null>(null);
  const jobsRef = useRef<UploadJob[]>([]);
  const mounted = useRef(true);

  jobsRef.current = jobs;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      activeController.current?.abort();
    };
  }, []);

  const enqueue = useCallback((request: UploadRequest) => {
    sequence.current += 1;
    const id = `upload-${Date.now()}-${sequence.current}`;
    const total = request.files.reduce((sum, file) => sum + file.size, 0);

    setJobs((current) => [
      ...current,
      {
        id,
        request,
        status: "queued",
        loaded: 0,
        total,
        progress: total > 0 ? 0 : null,
      },
    ]);

    return id;
  }, []);

  const updateProgress = useCallback(
    (jobId: string, progress: MediaUploadProgress) => {
      setJobs((current) =>
        current.map((job) =>
          job.id === jobId
            ? {
                ...job,
                loaded: progress.loaded,
                total: progress.total || job.total,
                progress: progress.progress,
                status:
                  (progress.progress ?? 0) >= 1 ? "processing" : job.status,
              }
            : job
        )
      );
    },
    []
  );

  useEffect(() => {
    if (activeJobId.current) return;

    const next = jobs.find((job) => job.status === "queued");
    if (!next) return;

    const controller = new AbortController();
    activeJobId.current = next.id;
    activeController.current = controller;
    setJobs((current) =>
      current.map((job) =>
        job.id === next.id
          ? { ...job, status: "uploading", error: undefined }
          : job
      )
    );

    let fullySent = false;

    mediaUploader(
      "Multimedia",
      {
        data: {
          id: next.request.userId,
          semana: next.request.semana,
          categoryImage: next.request.categoryImage,
          categoryVideo: next.request.categoryVideo,
          images: next.request.files.map((rawFile) => ({ rawFile })),
        },
      },
      {
        signal: controller.signal,
        onProgress: (progress) => {
          fullySent = (progress.progress ?? 0) >= 1;
          updateProgress(next.id, progress);
        },
      }
    )
      .then(() => {
        if (!mounted.current) return;
        activeJobId.current = null;
        activeController.current = null;
        const completed = {
          ...next,
          status: "completed" as const,
          loaded: next.request.files.reduce(
            (sum, file) => sum + file.size,
            0
          ),
          progress: 1,
        };
        setJobs((current) =>
          current.map((job) => (job.id === next.id ? completed : job))
        );
        onCompleted?.(completed);
      })
      .catch((error: Error) => {
        if (!mounted.current) return;
        activeJobId.current = null;
        activeController.current = null;
        const cancelled = error.name === "AbortError";
        const uncertain = fullySent;
        const failed = {
          ...next,
          status: uncertain
            ? ("uncertain" as const)
            : cancelled
            ? ("cancelled" as const)
            : ("error" as const),
          error: uncertain
            ? uncertainResultMessage
            : cancelled
            ? undefined
            : error.message,
        };
        setJobs((current) =>
          current.map((job) => (job.id === next.id ? failed : job))
        );
        if (!cancelled || uncertain) onFailed?.(failed);
      });
  }, [jobs, onCompleted, onFailed, updateProgress]);

  const cancel = useCallback((jobId: string) => {
    if (activeJobId.current === jobId) {
      const activeJob = jobsRef.current.find((job) => job.id === jobId);
      if (activeJob?.status === "uploading") {
        activeController.current?.abort();
      }
      return;
    }

    setJobs((current) =>
      current.map((job) =>
        job.id === jobId && job.status === "queued"
          ? { ...job, status: "cancelled" }
          : job
      )
    );
  }, []);

  const retry = useCallback((jobId: string) => {
    setJobs((current) =>
      current.map((job) =>
        job.id === jobId &&
        (job.status === "error" || job.status === "cancelled")
          ? {
              ...job,
              status: "queued",
              loaded: 0,
              progress: job.total > 0 ? 0 : null,
              error: undefined,
            }
          : job
      )
    );
  }, []);

  const dismiss = useCallback((jobId: string) => {
    setJobs((current) =>
      current.filter(
        (job) =>
          job.id !== jobId ||
          job.status === "uploading" ||
          job.status === "processing" ||
          job.status === "queued"
      )
    );
  }, []);

  const hasPendingUploads = jobs.some(
    (job) =>
      job.status === "queued" ||
      job.status === "uploading" ||
      job.status === "processing"
  );

  useEffect(() => {
    if (!hasPendingUploads) return;

    const protectUpload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", protectUpload);
    return () => window.removeEventListener("beforeunload", protectUpload);
  }, [hasPendingUploads]);

  const value = useMemo(
    () => ({ jobs, enqueue, cancel, retry, dismiss }),
    [cancel, dismiss, enqueue, jobs, retry]
  );

  return (
    <UploadManagerContext.Provider value={value}>
      {children}
    </UploadManagerContext.Provider>
  );
}

export function useUploadManager() {
  const value = useContext(UploadManagerContext);
  if (!value) {
    throw new Error("useUploadManager requiere UploadManagerProvider");
  }
  return value;
}
